import { useState } from "react";
import { useThemeColor } from "@/hooks/useThemeColor";

interface FilterSidebarProps {
  categories: string[];
  selectedCategory: string;
  onCategoryChange: (cat: string) => void;
  priceRange: [number, number];
  onPriceChange: (range: [number, number]) => void;
  maxPrice: number;
  minRating: number;
  onRatingChange: (r: number) => void;
  sortBy: string;
  onSortChange: (s: string) => void;
  onReset: () => void;
  activeFiltersCount: number;
}

const categoryLabels: Record<string, string> = {
  "الكل": "الكل",
  "PS5": "PS5",
  "PS4": "PS4",
  "PC": "كمبيوتر ألعاب",
  "Controllers": "أيادي التحكم",
  "Headsets": "سماعات",
};

const categoryIcons: Record<string, string> = {
  "الكل": "ri-apps-line",
  "PS5": "ri-gamepad-line",
  "PS4": "ri-gamepad-line",
  "PC": "ri-computer-line",
  "Controllers": "ri-gamepad-fill",
  "Headsets": "ri-headphone-line",
};

const sortOptions = [
  { value: "default", label: "الافتراضي", icon: "ri-list-unordered" },
  { value: "price-asc", label: "السعر: الأقل أولاً", icon: "ri-sort-asc" },
  { value: "price-desc", label: "السعر: الأعلى أولاً", icon: "ri-sort-desc" },
  { value: "rating", label: "الأعلى تقييماً", icon: "ri-star-line" },
  { value: "reviews", label: "الأكثر مراجعات", icon: "ri-chat-3-line" },
];

/* ── Section wrapper ── */
function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  const { primary, hexAlpha } = useThemeColor();
  return (
    <div className="border-b border-white/5 pb-5 mb-5 last:border-0 last:mb-0 last:pb-0">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full mb-3 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-md"
            style={{ background: hexAlpha(primary, 0.12) }}>
            <i className={`${icon} text-xs`} style={{ color: primary }}></i>
          </div>
          <span className="text-white text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {title}
          </span>
        </div>
        <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-500 text-sm transition-transform`}></i>
      </button>
      {open && children}
    </div>
  );
}

/* ── Dual range slider ── */
function DualRangeSlider({
  min, max, value, onChange,
}: {
  min: number; max: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
}) {
  const { primary } = useThemeColor();
  const pctLow = ((value[0] - min) / (max - min)) * 100;
  const pctHigh = ((value[1] - min) / (max - min)) * 100;

  return (
    <div className="relative pt-1">
      {/* Track */}
      <div className="relative h-1.5 rounded-full bg-white/10 mb-4">
        <div
          className="absolute h-full rounded-full"
          style={{
            left: `${pctLow}%`,
            right: `${100 - pctHigh}%`,
            background: primary,
          }}
        />
      </div>
      {/* Min slider */}
      <input
        type="range" min={min} max={max} step={50}
        value={value[0]}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v < value[1]) onChange([v, value[1]]);
        }}
        className="absolute top-0 w-full h-1.5 appearance-none bg-transparent cursor-pointer"
        style={{ accentColor: primary }}
      />
      {/* Max slider */}
      <input
        type="range" min={min} max={max} step={50}
        value={value[1]}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v > value[0]) onChange([value[0], v]);
        }}
        className="absolute top-0 w-full h-1.5 appearance-none bg-transparent cursor-pointer"
        style={{ accentColor: primary }}
      />
    </div>
  );
}

export default function FilterSidebar({
  categories,
  selectedCategory,
  onCategoryChange,
  priceRange,
  onPriceChange,
  maxPrice,
  minRating,
  onRatingChange,
  sortBy,
  onSortChange,
  onReset,
  activeFiltersCount,
}: FilterSidebarProps) {
  const { primary, hexAlpha } = useThemeColor();

  return (
    <aside className="w-full lg:w-64 shrink-0">
      <div
        className="rounded-2xl p-5 sticky top-24"
        style={{ background: "#111", border: `1px solid rgba(255,255,255,0.06)` }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <i className="ri-filter-3-line text-base" style={{ color: primary }}></i>
            <h3 className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>
              الفلاتر
            </h3>
            {activeFiltersCount > 0 && (
              <span
                className="text-black text-[10px] font-black w-5 h-5 flex items-center justify-center rounded-full"
                style={{ background: primary }}
              >
                {activeFiltersCount}
              </span>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <button
              onClick={onReset}
              className="flex items-center gap-1 text-xs transition-all cursor-pointer whitespace-nowrap px-2.5 py-1 rounded-lg"
              style={{
                fontFamily: "'Cairo', sans-serif",
                color: primary,
                background: hexAlpha(primary, 0.1),
                border: `1px solid ${hexAlpha(primary, 0.2)}`,
              }}
            >
              <i className="ri-refresh-line text-[10px]"></i>
              إعادة تعيين
            </button>
          )}
        </div>

        {/* ── Sort ── */}
        <Section title="الترتيب" icon="ri-sort-desc">
          <div className="space-y-1">
            {sortOptions.map((opt) => {
              const active = sortBy === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => onSortChange(opt.value)}
                  className="w-full text-right px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer flex items-center gap-2.5"
                  style={{
                    fontFamily: "'Cairo', sans-serif",
                    background: active ? hexAlpha(primary, 0.12) : "transparent",
                    color: active ? primary : "#9ca3af",
                    border: active ? `1px solid ${hexAlpha(primary, 0.25)}` : "1px solid transparent",
                  }}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${opt.icon} text-xs`}></i>
                  </div>
                  <span className="flex-1">{opt.label}</span>
                  {active && <i className="ri-check-line text-xs flex-shrink-0"></i>}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── Categories ── */}
        <Section title="الفئة" icon="ri-apps-2-line">
          <div className="space-y-1">
            {categories.map((cat) => {
              const active = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => onCategoryChange(cat)}
                  className="w-full text-right px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer flex items-center gap-2.5"
                  style={{
                    fontFamily: "'Cairo', sans-serif",
                    background: active ? hexAlpha(primary, 0.12) : "transparent",
                    color: active ? primary : "#9ca3af",
                    border: active ? `1px solid ${hexAlpha(primary, 0.25)}` : "1px solid transparent",
                  }}
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${categoryIcons[cat] ?? "ri-gamepad-line"} text-xs`}></i>
                  </div>
                  <span className="flex-1">{categoryLabels[cat] ?? cat}</span>
                  {active && <i className="ri-check-line text-xs flex-shrink-0"></i>}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── Price Range ── */}
        <Section title="نطاق السعر" icon="ri-price-tag-3-line">
          {/* Values display */}
          <div className="flex items-center justify-between mb-4">
            <div
              className="flex-1 text-center py-2 rounded-xl text-sm font-bold"
              style={{
                background: hexAlpha(primary, 0.08),
                border: `1px solid ${hexAlpha(primary, 0.15)}`,
                color: primary,
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {priceRange[0].toLocaleString()}
              <span className="text-[10px] font-normal mr-0.5">ر.س</span>
            </div>
            <div className="px-2 text-gray-600 text-xs">—</div>
            <div
              className="flex-1 text-center py-2 rounded-xl text-sm font-bold"
              style={{
                background: hexAlpha(primary, 0.08),
                border: `1px solid ${hexAlpha(primary, 0.15)}`,
                color: primary,
                fontFamily: "'Cairo', sans-serif",
              }}
            >
              {priceRange[1].toLocaleString()}
              <span className="text-[10px] font-normal mr-0.5">ر.س</span>
            </div>
          </div>

          {/* Dual slider */}
          <DualRangeSlider
            min={0}
            max={maxPrice}
            value={priceRange}
            onChange={onPriceChange}
          />

          {/* Quick price presets */}
          <div className="grid grid-cols-2 gap-1.5 mt-3">
            {[
              { label: "أقل من 500", range: [0, 500] as [number, number] },
              { label: "500 — 1500", range: [500, 1500] as [number, number] },
              { label: "1500 — 3000", range: [1500, 3000] as [number, number] },
              { label: "أكثر من 3000", range: [3000, maxPrice] as [number, number] },
            ].map((preset) => {
              const active =
                priceRange[0] === preset.range[0] && priceRange[1] === preset.range[1];
              return (
                <button
                  key={preset.label}
                  onClick={() => onPriceChange(preset.range)}
                  className="text-[11px] py-1.5 px-2 rounded-lg transition-all cursor-pointer whitespace-nowrap text-center"
                  style={{
                    fontFamily: "'Cairo', sans-serif",
                    background: active ? hexAlpha(primary, 0.15) : "rgba(255,255,255,0.04)",
                    color: active ? primary : "#6b7280",
                    border: active ? `1px solid ${hexAlpha(primary, 0.3)}` : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── Rating ── */}
        <Section title="التقييم" icon="ri-star-line">
          <div className="space-y-1.5">
            {/* All */}
            <button
              onClick={() => onRatingChange(0)}
              className="w-full text-right px-3 py-2.5 rounded-xl text-sm transition-all cursor-pointer flex items-center gap-2.5"
              style={{
                fontFamily: "'Cairo', sans-serif",
                background: minRating === 0 ? hexAlpha(primary, 0.12) : "transparent",
                color: minRating === 0 ? primary : "#9ca3af",
                border: minRating === 0 ? `1px solid ${hexAlpha(primary, 0.25)}` : "1px solid transparent",
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-apps-line text-xs"></i>
              </div>
              <span className="flex-1">جميع التقييمات</span>
              {minRating === 0 && <i className="ri-check-line text-xs flex-shrink-0"></i>}
            </button>

            {/* Star ratings */}
            {[5, 4, 3].map((r) => {
              const active = minRating === r;
              return (
                <button
                  key={r}
                  onClick={() => onRatingChange(r)}
                  className="w-full px-3 py-2.5 rounded-xl transition-all cursor-pointer flex items-center gap-2.5"
                  style={{
                    background: active ? hexAlpha(primary, 0.12) : "transparent",
                    border: active ? `1px solid ${hexAlpha(primary, 0.25)}` : "1px solid transparent",
                  }}
                >
                  {/* Stars */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className="ri-star-fill text-xs"
                        style={{ color: i < r ? primary : "#374151" }}
                      />
                    ))}
                  </div>
                  <span
                    className="text-xs flex-1 text-right"
                    style={{
                      fontFamily: "'Cairo', sans-serif",
                      color: active ? primary : "#9ca3af",
                    }}
                  >
                    {r === 5 ? "5 نجوم فقط" : `${r} نجوم فأكثر`}
                  </span>
                  {active && (
                    <i className="ri-check-line text-xs flex-shrink-0" style={{ color: primary }}></i>
                  )}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Reset button at bottom */}
        {activeFiltersCount > 0 && (
          <button
            onClick={onReset}
            className="w-full mt-2 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer flex items-center justify-center gap-2"
            style={{
              fontFamily: "'Cairo', sans-serif",
              background: "rgba(239,68,68,0.08)",
              color: "#f87171",
              border: "1px solid rgba(239,68,68,0.2)",
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.15)";
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = "rgba(239,68,68,0.08)";
            }}
          >
            <i className="ri-delete-bin-line text-sm"></i>
            مسح جميع الفلاتر ({activeFiltersCount})
          </button>
        )}
      </div>
    </aside>
  );
}
